import { createContext, useContext, useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { deserialize } from 'deserialize-json-api'
import { chain } from 'lodash'
import { useLocalStorage } from 'usehooks-ts'

import API from '@shared/services/src/API'
import { featureFlags as featureFlagsMock } from '@shared/services/src/Settings.mocks'
import { QK } from '@shared/utils'

/**
 * Provide a context for the apps infrastructure feature flags.
 */
export default function FeatureFlagsProvider({ children }) {
  const [persistedCache, setPersistedCache] = useLocalStorage('cache-persisted', true)

  const { data, isFetching } = useSuspenseQuery({
    queryKey: QK.platform.features.list,
    queryFn: () => API.platform.features.list(),
    select: (data) => {
      const cachePersistedFlag = data.find((f) => f.key === FeatureFlag.ReactCachePersisted)
      if (cachePersistedFlag && cachePersistedFlag.value !== persistedCache) {
        setPersistedCache(cachePersistedFlag.value)
      }
      return data
    },
  })

  return <FeatureFlagsContext.Provider value={{ flags: data, isFetching }}>{children}</FeatureFlagsContext.Provider>
}

export function FeatureFlagsMockerProvider({ children, flags = featureFlagsMock }) {
  const flagsMapped = deserialize(flags, { transformKeys: 'camelCase' })?.data ?? []

  return <FeatureFlagsContext.Provider value={{ flags: flagsMapped, isFetching: false }}>{children}</FeatureFlagsContext.Provider>
}

const FeatureFlagsContext = createContext(undefined)

export const useFeatureFlagsQuery = () => useContext(FeatureFlagsContext)

/**
 * Returns a map of feature flags with the key as the flag name and the boolean as the flag value.
 *
 * @example:
 * const flags = useFeatureFlags()
 * const isFeature1Enabled = flags[FeatureFlag.Feature1]
 * const isFeature2Enabled = flags[FeatureFlag.Feature2]
 *
 * @returns {{ [p: string]: boolean }}
 */
export const useFeatureFlags = () => {
  const { flags } = useContext(FeatureFlagsContext)

  return useMemo(() => {
    return chain(flags)
      .groupBy('key')
      .mapValues((v) => v[0].value)
      .value()
  }, [flags])
}

/**
 * Returns a boolean value for the given feature flag.
 *
 * @example:
 * const isFeature1Enabled = useFeatureFlag(FeatureFlag.Feature1)
 *
 * @param key - The feature flag key
 * @returns {boolean} - The feature flag value
 */
export const useFeatureFlag = (key) => {
  const flags = useFeatureFlags()
  return flags[key] || false
}

export const FeatureFlag = {
  ReactCachePersisted: 'react_cache_persisted',
  DoseSpotEnabled: 'dosespot_enabled',
  QuestLabs: 'quest_labs',
  CashPay: 'cash_pay',
  BranchEnrollmentSwitch: 'branch_enrollment_switch',
}
